import { IconUser } from '@tabler/icons-react';
import { cn } from './cn';

/**
 * Avatar — Design System v2 (docs/design-system-v2.html §5.avatar /
 * .entity-avatar).
 *
 * Aceita `src` (imagem) OU gera iniciais a partir de `name`. O `gradient`
 * (índice 0..3) varia a cor de fundo entre quatro paletas — útil pra
 * listas onde queremos diferenciar pessoas/tenants no relance.
 *
 * Quando o `name` não começa por uma letra (típico no inbox: contato
 * sem nome ainda → cai no telefone mascarado tipo "(91) 9..." e as
 * iniciais sairiam como "(9"), renderiza ícone genérico de usuário
 * pra evitar ruído visual.
 */

type Size = 'sm' | 'md' | 'lg';

interface AvatarProps {
  name: string;
  src?: string;
  size?: Size;
  gradient?: number;
  className?: string;
}

const SIZE: Record<Size, string> = {
  sm: 'w-7 h-7 text-[11px]',
  md: 'w-[38px] h-[38px] text-[13px]',
  lg: 'w-12 h-12 text-[15px]',
};

// Tamanho do ícone de fallback proporcional ao tamanho do avatar.
const ICON_SIZE: Record<Size, number> = { sm: 14, md: 18, lg: 22 };

const GRADIENTS = [
  'from-[var(--brand-400)] to-[var(--brand-700)]',
  'from-[#60a5fa] to-[#1d4ed8]',
  'from-[#f472b6] to-[#be185d]',
  'from-[#fbbf24] to-[#b45309]',
];

/**
 * `true` quando o nome efetivamente vira iniciais legíveis. Heurística:
 * primeiro caractere precisa ser uma LETRA (inclui acentos latinos).
 * Cobre os principais casos ruins:
 *  - "(91) 99999-9999"  → começa com "(" → false (usa ícone)
 *  - "5511988887777"    → começa com dígito → false (usa ícone)
 *  - "—" / "?" / vazio  → false (usa ícone)
 *  - "Ana"              → true
 *  - "Ézio"             → true
 */
function pareceNomeReal(nome: string): boolean {
  const t = nome.trim();
  if (!t) return false;
  return /^[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(t);
}

function iniciais(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  const a = parts[0][0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (a + b).toUpperCase();
}

export function Avatar({
  name,
  src,
  size = 'md',
  gradient = 0,
  className,
}: AvatarProps): JSX.Element {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('rounded-full object-cover shrink-0', SIZE[size], className)}
      />
    );
  }
  const grad = GRADIENTS[gradient % GRADIENTS.length];
  const usarIcone = !pareceNomeReal(name);
  return (
    <span
      aria-label={name}
      className={cn(
        'rounded-full grid place-items-center font-display font-bold text-white shrink-0 bg-gradient-to-br',
        grad,
        SIZE[size],
        className,
      )}
    >
      {usarIcone ? <IconUser size={ICON_SIZE[size]} /> : iniciais(name)}
    </span>
  );
}
